import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { communityApi } from '../../lib/api';
import { apiErrorMessage } from '../../lib/communityValidation';

// Upvote-only toggle. Optimistic: flips immediately, then reconciles with the
// server's authoritative count (or rolls back on error).
const VoteButton = ({ targetType, targetId, count, voted, isOwn, onChange }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (isOwn || busy) return;
    setBusy(true);
    const prev = { voted, count };
    onChange({ voted: !voted, count: count + (voted ? -1 : 1) });
    try {
      const res = await communityApi.toggleVote(targetType, targetId);
      onChange({ voted: res.voted, count: res.vote_count });
    } catch (err) {
      onChange(prev);
      toast.error(apiErrorMessage(err, 'Failed to record vote'));
    } finally {
      setBusy(false);
    }
  };

  const label = isOwn
    ? "You can't upvote your own post"
    : voted ? 'Remove upvote' : 'Upvote — this is useful';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy || isOwn}
      title={label}
      aria-label={label}
      aria-pressed={voted}
      className={`flex flex-col items-center justify-center min-w-[44px] min-h-[44px] rounded-sm border transition-colors
        ${voted
          ? 'border-autoxium-blue bg-autoxium-blue/10 text-autoxium-blue'
          : 'border-structure text-muted-foreground hover:border-autoxium-blue hover:text-autoxium-blue'}
        disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-structure disabled:hover:text-muted-foreground`}
      data-testid={`vote-${targetType}-${targetId}`}
    >
      <ChevronUp className="h-4 w-4" />
      <span className="text-xs font-mono font-bold leading-none">{count}</span>
    </button>
  );
};

export default VoteButton;
